import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Seo } from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { PORTFOLIO_CATEGORIES } from "@/data/content";
import { categoryLabel } from "@/components/PortfolioGrid";

const STATUS_LABELS = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité" };

const inputClass =
  "bg-[#020617] border-slate-800 text-white text-sm placeholder:text-slate-600 focus-visible:ring-[#38bdf8] h-10";

const EMPTY_ITEM = {
  title: "",
  category: "site-web",
  description: "",
  long_description: "",
  features: "",
  image_url: "",
  device: "browser",
  is_demo: true,
  order: 0,
};

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-[#0f172a] border border-slate-800 rounded-2xl p-8 space-y-5"
      >
        <div className="text-center">
          <div className="w-10 h-10 rounded-lg bg-[#1d4ed8] border border-[#38bdf8]/40 flex items-center justify-center font-bold text-white font-mono-x text-sm mx-auto mb-3">
            KX
          </div>
          <h1 className="font-display text-2xl text-white">Espace Koranex</h1>
          <p className="text-xs text-slate-400 mt-1">Connexion administrateur</p>
        </div>
        <div>
          <label htmlFor="admin-email" className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">
            E-mail
          </label>
          <Input
            id="admin-email"
            type="email"
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">
            Mot de passe
          </label>
          <Input
            id="admin-password"
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="text-xs text-red-400">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full py-3 bg-[#1d4ed8] hover:bg-blue-600 disabled:opacity-60 text-white font-mono-x font-bold uppercase text-xs tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Se connecter
        </button>
      </form>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/leads")
      .then((res) => setLeads(res.data))
      .catch((err) => toast.error("Chargement impossible", { description: formatApiError(err) }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error("Erreur", { description: formatApiError(err) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error("Erreur", { description: formatApiError(err) });
    }
  };

  if (loading) return <div data-testid="leads-loading" className="h-40 rounded-xl bg-[#0f172a] border border-slate-800 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="leads-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono-x text-slate-400">{leads.length} demande(s)</p>
        <button
          type="button"
          onClick={load}
          data-testid="leads-refresh"
          className="inline-flex items-center gap-2 text-xs font-mono-x text-[#38bdf8] hover:text-white transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>
      {leads.length === 0 && (
        <p data-testid="leads-empty" className="text-sm text-slate-400 text-center py-10 border border-dashed border-slate-800 rounded-xl">
          Aucune demande pour le moment. Les demandes envoyées via le formulaire apparaîtront ici.
        </p>
      )}
      {leads.map((lead) => (
        <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="rounded-xl bg-[#0f172a] border border-slate-800 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-bold text-white text-sm">
                {lead.name} {lead.company ? <span className="text-slate-400 font-normal">· {lead.company}</span> : null}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {lead.email} {lead.phone ? `· ${lead.phone}` : ""} {lead.country ? `· ${lead.country}` : ""}
              </p>
            </div>
            <Badge variant="outline" className="border-[#38bdf8]/40 text-[#38bdf8] text-[10px] font-mono-x uppercase">
              {lead.service}
            </Badge>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed mb-3 whitespace-pre-line">{lead.description}</p>
          <p className="text-[10px] font-mono-x text-slate-500 mb-3">
            {lead.budget ? `Budget : ${lead.budget} · ` : ""}
            {lead.timeline ? `Délai : ${lead.timeline} · ` : ""}
            {new Date(lead.created_at).toLocaleString("fr-FR")}
          </p>
          <div className="flex items-center gap-3">
            <Select value={lead.status} onValueChange={(v) => setStatus(lead.id, v)}>
              <SelectTrigger data-testid={`lead-status-${lead.id}`} className={`${inputClass} w-40`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v} className="focus:bg-[#1d4ed8] focus:text-white">
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => remove(lead.id)}
              data-testid={`lead-delete-${lead.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-mono-x text-red-400 hover:text-red-300 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PortfolioTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?}
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/portfolio")
      .then((res) => setItems(res.data))
      .catch((err) => toast.error("Chargement impossible", { description: formatApiError(err) }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const openNew = () => {
    setForm(EMPTY_ITEM);
    setEditing({});
  };

  const openEdit = (item) => {
    setForm({ ...item, features: (item.features || []).join("\n") });
    setEditing(item);
  };

  const save = async () => {
    const payload = {
      ...form,
      features: form.features
        .split("\n")
        .map((f) => f.trim())
        .filter(Boolean),
      order: Number(form.order) || 0,
    };
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/portfolio/${editing.id}`, payload);
        toast.success("Réalisation mise à jour");
      } else {
        await api.post("/portfolio", payload);
        toast.success("Réalisation ajoutée");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Enregistrement impossible", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/portfolio/${id}`);
      setItems((l) => l.filter((x) => x.id !== id));
      toast.success("Réalisation supprimée");
    } catch (err) {
      toast.error("Erreur", { description: formatApiError(err) });
    }
  };

  if (loading) return <div data-testid="portfolio-admin-loading" className="h-40 rounded-xl bg-[#0f172a] border border-slate-800 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portfolio-admin-tab">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={openNew}
          data-testid="portfolio-add-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1d4ed8] hover:bg-blue-600 text-white text-xs font-mono-x font-bold uppercase tracking-wider transition-colors"
        >
          <Plus className="w-4 h-4" /> Ajouter une réalisation
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item) => (
          <div key={item.id} data-testid={`portfolio-admin-card-${item.id}`} className="rounded-xl bg-[#0f172a] border border-slate-800 p-4 flex gap-4">
            <img src={item.image_url} alt={item.title} className="w-20 h-16 object-cover rounded-lg border border-slate-800 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-sm truncate">{item.title}</h3>
              <p className="text-[10px] font-mono-x uppercase tracking-wider text-[#38bdf8]">{categoryLabel(item.category)}</p>
              <p className="text-[10px] font-mono-x text-slate-500">{item.is_demo ? "Concept de démonstration" : "Projet client réel"}</p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  data-testid={`portfolio-edit-${item.id}`}
                  className="text-xs font-mono-x text-[#38bdf8] hover:text-white transition-colors"
                >
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  data-testid={`portfolio-delete-${item.id}`}
                  className="text-xs font-mono-x text-red-400 hover:text-red-300 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="portfolio-edit-dialog" className="bg-[#0f172a] border-slate-800 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing?.id ? "Modifier la réalisation" : "Nouvelle réalisation"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Titre *</label>
              <Input data-testid="portfolio-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Catégorie</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="portfolio-form-category" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
                    {PORTFOLIO_CATEGORIES.filter((c) => c.value !== "all").map((c) => (
                      <SelectItem key={c.value} value={c.value} className="focus:bg-[#1d4ed8] focus:text-white">
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Type de visuel</label>
                <Select value={form.device} onValueChange={(v) => setForm({ ...form, device: v })}>
                  <SelectTrigger data-testid="portfolio-form-device" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
                    <SelectItem value="browser" className="focus:bg-[#1d4ed8] focus:text-white">Site web (navigateur)</SelectItem>
                    <SelectItem value="phone" className="focus:bg-[#1d4ed8] focus:text-white">Application (smartphone)</SelectItem>
                    <SelectItem value="photo" className="focus:bg-[#1d4ed8] focus:text-white">Photo (image complète)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Description courte *</label>
              <Input data-testid="portfolio-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Description détaillée</label>
              <Textarea data-testid="portfolio-form-long" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} rows={3} className={`${inputClass} h-auto`} />
            </div>
            <div>
              <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Fonctionnalités (une par ligne)</label>
              <Textarea data-testid="portfolio-form-features" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={3} className={`${inputClass} h-auto`} />
            </div>
            <div>
              <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">URL de l'image *</label>
              <Input data-testid="portfolio-form-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-4 items-center">
              <div>
                <label className="block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5">Ordre d'affichage</label>
                <Input data-testid="portfolio-form-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inputClass} />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-300 mt-5 cursor-pointer">
                <Checkbox
                  data-testid="portfolio-form-demo"
                  checked={form.is_demo}
                  onCheckedChange={(v) => setForm({ ...form, is_demo: v === true })}
                  className="border-slate-600 data-[state=checked]:bg-[#1d4ed8] data-[state=checked]:border-[#1d4ed8]"
                />
                Concept de démonstration
              </label>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={saving || !form.title || !form.description || !form.image_url}
              data-testid="portfolio-form-save"
              className="w-full py-3 bg-[#1d4ed8] hover:bg-blue-600 disabled:opacity-60 text-white font-mono-x font-bold uppercase text-xs tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null); // null = chargement, false = non connecté

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#38bdf8] animate-spin" data-testid="admin-loading" />
      </div>
    );
  }

  if (user === false) {
    return (
      <>
        <Seo title="Espace administrateur — Koranex" description="Espace réservé à l'administration du site Koranex." />
        <LoginForm onSuccess={setUser} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="admin-dashboard">
      <Seo title="Tableau de bord — Koranex" description="Administration du site Koranex." />
      <header className="border-b border-slate-800 bg-[#020617]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#1d4ed8] border border-[#38bdf8]/40 flex items-center justify-center font-bold text-white font-mono-x text-sm">
              KX
            </div>
            <span className="font-bold tracking-wider text-white font-mono-x text-sm">ADMINISTRATION</span>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-xs font-mono-x text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="leads">
          <TabsList className="bg-[#0f172a] border border-slate-800 mb-8">
            <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-[#1d4ed8] data-[state=active]:text-white text-xs font-mono-x uppercase">
              Demandes de devis
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="data-[state=active]:bg-[#1d4ed8] data-[state=active]:text-white text-xs font-mono-x uppercase">
              Réalisations
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads">
            <LeadsTab />
          </TabsContent>
          <TabsContent value="portfolio">
            <PortfolioTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
